import { useEffect, useState } from "react";
import { BarChart3, MessageSquare, CheckCircle2, SearchX, Ban, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const CHANNEL_LABEL = { playground: "Test", hosted: "Web", api: "API" };

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return ""; }
}

function StatCard({ icon: Icon, label, value, sub, tone }) {
  const toneCls = {
    default: "text-foreground",
    green: "text-emerald-600",
    amber: "text-amber-600",
    red: "text-red-600",
  }[tone || "default"];
  return (
    <div className="soft-card p-4">
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <Icon className={`h-4 w-4 ${toneCls}`} /> {label}
      </div>
      <div className={`mt-2 text-2xl font-extrabold tracking-tight ${toneCls}`}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function ProjectAnalytics({ pid }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    api.getProjectAnalytics(pid).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  };
  useEffect(load, [pid]);

  const t = data?.totals;
  const problems = data?.problems || [];
  const topUnanswered = data?.top_unanswered || [];

  return (
    <section className="mt-12" data-testid="project-analytics">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-bold">
          <BarChart3 className="h-5 w-5" /> Yanıt Analitiği
        </h2>
        <Button data-testid="analytics-refresh-btn" variant="outline" size="sm" className="rounded-full" onClick={load} disabled={loading}>
          <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Yenile
        </Button>
      </div>

      {loading && !data ? (
        <div className="soft-card px-6 py-12 text-center text-sm text-muted-foreground">Yükleniyor...</div>
      ) : !t || t.total === 0 ? (
        <div className="soft-card px-6 py-12 text-center text-sm text-muted-foreground" data-testid="analytics-empty">
          Henüz analitik verisi yok. Asistanlarınız soru aldıkça yanıtlanamayan ve reddedilen sorular burada görünecek.
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard icon={MessageSquare} label="Toplam soru" value={t.total} />
            <StatCard icon={CheckCircle2} label="Yanıtlanan" value={t.answered} sub={`%${Math.round(t.answer_rate * 100)} yanıt oranı`} tone="green" />
            <StatCard icon={SearchX} label="Kaynak bulunamadı" value={t.no_source} tone="amber" />
            <StatCard icon={Ban} label="Reddedildi" value={t.refused} tone="red" />
          </div>

          {topUnanswered.length > 0 && (
            <div className="mt-5 soft-card p-5" data-testid="analytics-top-unanswered">
              <h3 className="font-bold">En çok cevaplanamayan sorular</h3>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Kullanıcıların tekrar tekrar sorduğu ama kaynaklarda karşılığı olmayan sorular. Bu konularda doküman ekleyin.
              </p>
              <div className="mt-4 space-y-2">
                {topUnanswered.map((u, i) => (
                  <div key={i} data-testid={`top-unanswered-${i}`} className="flex items-center justify-between gap-3 rounded-xl border border-border px-4 py-2.5">
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">{u.query}</span>
                    <Badge variant="outline" className="shrink-0 rounded-full">{u.count}×</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5 soft-card p-5">
            <h3 className="font-bold">Sorunlu sorular</h3>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Kaynaklarda cevabı bulunamayan veya yasaklı olduğu için reddedilen sorular. İçerik boşluklarını buradan yakalayın.
            </p>
            {problems.length === 0 ? (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-6 text-center text-sm text-emerald-700" data-testid="analytics-all-answered">
                🎉 Tüm sorular başarıyla yanıtlandı.
              </div>
            ) : (
              <div className="mt-4 space-y-2">
                {problems.map((p, i) => (
                  <div
                    key={i}
                    data-testid={`analytics-row-${i}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelected(p)}
                    className="flex cursor-pointer items-start justify-between gap-3 rounded-xl border border-border px-4 py-3 transition-colors hover:bg-secondary/50"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">
                        {p.query || <span className="text-muted-foreground">(kayıtlı soru yok)</span>}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                        <span>{p.assistant_name}</span>
                        <span>·</span>
                        <span>{CHANNEL_LABEL[p.channel] || p.channel}</span>
                        <span>·</span>
                        <span>{fmtDate(p.created_at)}</span>
                      </div>
                    </div>
                    {p.type === "refused" ? (
                      <Badge variant="outline" className="shrink-0 rounded-full border-red-200 bg-red-50 text-red-700">
                        <Ban className="mr-1 h-3 w-3" /> Reddedildi
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="shrink-0 rounded-full border-amber-200 bg-amber-50 text-amber-700">
                        <SearchX className="mr-1 h-3 w-3" /> Kaynak yok
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="analytics-detail-dialog">
          <DialogHeader>
            <DialogTitle>Soru detayı</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div>
                <div className="text-xs font-medium text-muted-foreground">Soru</div>
                <div className="mt-1 rounded-xl bg-secondary/50 px-3 py-2 text-sm">
                  {selected.query || "(kayıtlı soru yok)"}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-muted-foreground">Asistanın verdiği yanıt</div>
                <div className="mt-1 max-h-52 overflow-y-auto whitespace-pre-wrap rounded-xl border border-border px-3 py-2 text-sm">
                  {selected.answer || "—"}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                {selected.type === "refused" ? (
                  <Badge variant="outline" className="rounded-full border-red-200 bg-red-50 text-red-700"><Ban className="mr-1 h-3 w-3" /> Reddedildi</Badge>
                ) : (
                  <Badge variant="outline" className="rounded-full border-amber-200 bg-amber-50 text-amber-700"><SearchX className="mr-1 h-3 w-3" /> Kaynak yok</Badge>
                )}
                <span>·</span>
                <span>{selected.assistant_name}</span>
                <span>·</span>
                <span>{CHANNEL_LABEL[selected.channel] || selected.channel}</span>
                <span>·</span>
                <span>Güven: %{Math.round((selected.confidence || 0) * 100)}</span>
                <span>·</span>
                <span>{fmtDate(selected.created_at)}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
